<script setup lang="ts">
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
</script>

<template>
  <!-- Sector Management Section -->
  <div id="sector-management-section">
    <main id="methodologies-main" class="flex-1 p-5 overflow-y-auto">
      <!-- Controls Section -->
      <div
        id="controls-section"
        class="bg-white p-5 rounded-md border border-light-gray mb-6"
      >
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-dark-navy">
            {{ $t('government.administration.sectorManagement.controls.title') }}
          </h3>
          <div class="flex space-x-3">
            <button
              class="px-4 py-2 bg-forest-green text-white rounded-md hover:bg-green-700 text-sm"
            >
              <i class="fa-solid fa-plus mr-2"></i>{{ $t('government.administration.sectorManagement.controls.buttons.addMethodology') }}
            </button>
            <button
              class="px-4 py-2 border border-light-gray rounded-md hover:bg-gray-50 text-sm"
            >
              <i class="fa-solid fa-download mr-2"></i>{{ $t('government.administration.sectorManagement.controls.buttons.export') }}
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >{{ $t('government.administration.sectorManagement.controls.filters.sectorLabel') }}</label
            >
            <select
              class="w-full px-3 py-2 border border-light-gray rounded-md text-sm"
            >
              <option>{{ $t('government.administration.sectorManagement.controls.filters.allSectors') }}</option>
              <option>{{ $t('government.administration.sectorManagement.controls.filters.renewableEnergy') }}</option>
              <option>{{ $t('government.administration.sectorManagement.controls.filters.forestry') }}</option>
              <option>{{ $t('government.administration.sectorManagement.controls.filters.agriculture') }}</option>
              <option>{{ $t('government.administration.sectorManagement.controls.filters.wasteManagement') }}</option>
              <option>{{ $t('government.administration.sectorManagement.controls.filters.transportation') }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >{{ $t('government.administration.sectorManagement.controls.filters.statusLabel') }}</label
            >
            <select
              class="w-full px-3 py-2 border border-light-gray rounded-md text-sm"
            >
              <option>{{ $t('government.administration.sectorManagement.controls.filters.allStatus') }}</option>
              <option>{{ $t('government.administration.sectorManagement.controls.filters.approved') }}</option>
              <option>{{ $t('government.administration.sectorManagement.controls.filters.notApproved') }}</option>
              <option>{{ $t('government.administration.sectorManagement.controls.filters.underReview') }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >{{ $t('government.administration.sectorManagement.controls.filters.searchLabel') }}</label
            >
            <div class="relative">
              <input
                type="text"
:placeholder="$t('government.administration.sectorManagement.controls.filters.searchPlaceholder')"
                class="w-full px-3 py-2 pl-10 border border-light-gray rounded-md text-sm"
              />
              <i
                class="fa-solid fa-search absolute left-3 top-3 text-gray-400"
              ></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Approved Methodologies -->
      <div
        id="approved-methodologies"
        class="bg-white p-5 rounded-md border border-light-gray mb-6"
      >
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-dark-navy">
            <i class="fa-solid fa-check-circle text-green-600 mr-2"></i>
            {{ $t('government.administration.sectorManagement.approvedMethodologies.title') }}
          </h3>
          <span
            class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
            >24 {{ $t('government.administration.sectorManagement.approvedMethodologies.count') }}</span
          >
        </div>

        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.approvedMethodologies.table.headers.methodologyId') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.approvedMethodologies.table.headers.sector') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.approvedMethodologies.table.headers.title') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.approvedMethodologies.table.headers.version') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.approvedMethodologies.table.headers.approvalDate') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.approvedMethodologies.table.headers.actions') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr>
                <td class="px-4 py-3 text-sm font-medium text-dark-navy">
                  ACM001
                </td>
                <td class="px-4 py-3 text-sm">
                  <span
                    class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                    >Renewable Energy</span
                  >
                </td>
                <td class="px-4 py-3 text-sm">
                  Grid-connected electricity generation from renewable sources
                </td>
                <td class="px-4 py-3 text-sm">v2.1</td>
                <td class="px-4 py-3 text-sm">2024-01-15</td>
                <td class="px-4 py-3 text-sm">
                  <div class="flex space-x-2">
                    <button class="text-forest-green hover:text-green-700">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-blue-700">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                    <button class="text-error-red hover:text-red-700">
                      <i class="fa-solid fa-ban"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-4 py-3 text-sm font-medium text-dark-navy">
                  ACM002
                </td>
                <td class="px-4 py-3 text-sm">
                  <span
                    class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                    >Forestry</span
                  >
                </td>
                <td class="px-4 py-3 text-sm">
                  Afforestation and reforestation of degraded land
                </td>
                <td class="px-4 py-3 text-sm">v1.3</td>
                <td class="px-4 py-3 text-sm">2024-02-20</td>
                <td class="px-4 py-3 text-sm">
                  <div class="flex space-x-2">
                    <button class="text-forest-green hover:text-green-700">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-blue-700">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                    <button class="text-error-red hover:text-red-700">
                      <i class="fa-solid fa-ban"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-4 py-3 text-sm font-medium text-dark-navy">
                  ACM003
                </td>
                <td class="px-4 py-3 text-sm">
                  <span
                    class="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs"
                    >Agriculture</span
                  >
                </td>
                <td class="px-4 py-3 text-sm">
                  Improved agricultural land management
                </td>
                <td class="px-4 py-3 text-sm">v1.1</td>
                <td class="px-4 py-3 text-sm">2024-03-10</td>
                <td class="px-4 py-3 text-sm">
                  <div class="flex space-x-2">
                    <button class="text-forest-green hover:text-green-700">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-blue-700">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                    <button class="text-error-red hover:text-red-700">
                      <i class="fa-solid fa-ban"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Non-Approved Methodologies -->
      <div
        id="non-approved-methodologies"
        class="bg-white p-5 rounded-md border border-light-gray mb-6"
      >
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-dark-navy">
            <i class="fa-solid fa-times-circle text-error-red mr-2"></i>
            {{ $t('government.administration.sectorManagement.nonApprovedMethodologies.title') }}
          </h3>
          <span class="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm"
            >8 {{ $t('government.administration.sectorManagement.nonApprovedMethodologies.count') }}</span
          >
        </div>

        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.nonApprovedMethodologies.table.headers.methodologyId') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.nonApprovedMethodologies.table.headers.sector') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.nonApprovedMethodologies.table.headers.title') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.nonApprovedMethodologies.table.headers.reason') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.nonApprovedMethodologies.table.headers.reviewDate') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700"
                >
                  {{ $t('government.administration.sectorManagement.nonApprovedMethodologies.table.headers.actions') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr>
                <td class="px-4 py-3 text-sm font-medium text-dark-navy">
                  NAM001
                </td>
                <td class="px-4 py-3 text-sm">
                  <span
                    class="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs"
                    >Industrial</span
                  >
                </td>
                <td class="px-4 py-3 text-sm">
                  Coal mine methane recovery and utilization
                </td>
                <td class="px-4 py-3 text-sm text-error-red">
                  Incompatible with national policy
                </td>
                <td class="px-4 py-3 text-sm">2024-01-30</td>
                <td class="px-4 py-3 text-sm">
                  <div class="flex space-x-2">
                    <button class="text-forest-green hover:text-green-700">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-blue-700">
                      <i class="fa-solid fa-check"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <td class="px-4 py-3 text-sm font-medium text-dark-navy">
                  NAM002
                </td>
                <td class="px-4 py-3 text-sm">
                  <span
                    class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                    >Waste</span
                  >
                </td>
                <td class="px-4 py-3 text-sm">
                  Landfill gas capture and flaring
                </td>
                <td class="px-4 py-3 text-sm text-error-red">
                  Technical requirements not met
                </td>
                <td class="px-4 py-3 text-sm">2024-02-15</td>
                <td class="px-4 py-3 text-sm">
                  <div class="flex space-x-2">
                    <button class="text-forest-green hover:text-green-700">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-blue-700">
                      <i class="fa-solid fa-check"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Sectors NOT Approved -->
      <div
        id="sectors-not-approved"
        class="bg-white p-5 rounded-md border border-light-gray"
      >
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-dark-navy">
            <i class="fa-solid fa-ban text-error-red mr-2"></i>
            {{ $t('government.administration.sectorManagement.sectorsNotApproved.title') }}
          </h3>
          <button
            class="px-4 py-2 bg-forest-green text-white rounded-md hover:bg-green-700 text-sm"
          >
            <i class="fa-solid fa-plus mr-2"></i>{{ $t('government.administration.sectorManagement.sectorsNotApproved.addButton') }}
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div class="p-4 border border-error-red rounded-md bg-red-50">
            <div class="flex justify-between items-start mb-2">
              <h4 class="font-medium text-dark-navy">{{ $t('government.administration.sectorManagement.sectorsNotApproved.sectors.coalMining') }}</h4>
              <button class="text-error-red hover:text-red-700">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
            <p class="text-sm text-gray-600 mb-2">
              {{ $t('government.administration.sectorManagement.sectorsNotApproved.descriptions.coalMining') }}
            </p>
            <span class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
              >{{ $t('government.administration.sectorManagement.sectorsNotApproved.restrictions.permanentlyRestricted') }}</span
            >
          </div>

          <div class="p-4 border border-error-red rounded-md bg-red-50">
            <div class="flex justify-between items-start mb-2">
              <h4 class="font-medium text-dark-navy">{{ $t('government.administration.sectorManagement.sectorsNotApproved.sectors.oilGasExtraction') }}</h4>
              <button class="text-error-red hover:text-red-700">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
            <p class="text-sm text-gray-600 mb-2">
              {{ $t('government.administration.sectorManagement.sectorsNotApproved.descriptions.oilGasExtraction') }}
            </p>
            <span class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
              >{{ $t('government.administration.sectorManagement.sectorsNotApproved.restrictions.policyRestricted') }}</span
            >
          </div>

          <div class="p-4 border border-error-red rounded-md bg-red-50">
            <div class="flex justify-between items-start mb-2">
              <h4 class="font-medium text-dark-navy">{{ $t('government.administration.sectorManagement.sectorsNotApproved.sectors.nuclearEnergy') }}</h4>
              <button class="text-error-red hover:text-red-700">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
            <p class="text-sm text-gray-600 mb-2">
              {{ $t('government.administration.sectorManagement.sectorsNotApproved.descriptions.nuclearEnergy') }}
            </p>
            <span class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
              >{{ $t('government.administration.sectorManagement.sectorsNotApproved.restrictions.underReview') }}</span
            >
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
